import { useState } from 'react';
import {
  BookmarkIcon as SaveIcon,
  FolderOpenIcon,
  TrashIcon,
  ListBulletIcon as ViewListIcon,
  ViewColumnsIcon,
  ShareIcon,
  ArrowDownTrayIcon as DownloadIcon,
  PlayIcon,
  CogIcon,
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import type { ConnectionPlan } from '../../types';

interface PlanningToolbarProps {
  currentPlan: ConnectionPlan | null;
  onSave?: () => void;
  onLoad?: (planId: string) => void;
  onClear?: () => void;
  onToggleMode?: () => void;
  isCanvasMode?: boolean;
  readonly?: boolean;
}

export function PlanningToolbar({
  currentPlan,
  onSave,
  onLoad,
  onClear,
  onToggleMode,
  isCanvasMode = false,
  readonly = false
}: PlanningToolbarProps) {
  const [showLoadDialog, setShowLoadDialog] = useState(false);

  const hasApps = currentPlan && currentPlan.apps.length > 0;
  const canSave = hasApps && !readonly;

  return (
    <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-3">
      <div className="flex items-center justify-between">
        {/* Left Side - Plan Info */}
        <div className="flex items-center space-x-4">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
            {currentPlan?.name || 'New Connection Plan'}
          </h1>
          
          {hasApps && (
            <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
              <span>{currentPlan.apps.length} apps</span>
              <span>•</span>
              <span>{currentPlan.connections.length} connections</span>
            </div>
          )}
        </div>

        {/* Right Side - Actions */}
        <div className="flex items-center space-x-2">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={onToggleMode}
              className={clsx(
                'px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-200',
                {
                  'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm': !isCanvasMode,
                  'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white': isCanvasMode,
                }
              )}
            >
              <ViewListIcon className="h-4 w-4 mr-1.5 inline" />
              Grid
            </button>
            <button
              onClick={onToggleMode}
              className={clsx(
                'px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-200',
                {
                  'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm': isCanvasMode,
                  'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white': !isCanvasMode,
                }
              )}
            >
              <ViewColumnsIcon className="h-4 w-4 mr-1.5 inline" />
              Canvas
            </button>
          </div>

          {/* Divider */}
          <div className="h-6 w-px bg-gray-300 dark:bg-gray-600" />

          {/* Load Plan */}
          {onLoad && (
            <button
              onClick={() => setShowLoadDialog(true)}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors duration-200"
            >
              <FolderOpenIcon className="h-4 w-4 mr-1.5" />
              Load
            </button>
          )}

          {/* Save Plan */}
          {onSave && (
            <button
              onClick={onSave}
              disabled={!canSave}
              className={clsx(
                'inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors duration-200',
                {
                  'bg-blue-600 text-white hover:bg-blue-700': canSave,
                  'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed': !canSave,
                }
              )}
            >
              <SaveIcon className="h-4 w-4 mr-1.5" />
              Save
            </button>
          )}

          {/* Clear Plan */}
          {onClear && !readonly && hasApps && (
            <button
              onClick={onClear}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors duration-200"
            >
              <TrashIcon className="h-4 w-4 mr-1.5" />
              Clear
            </button>
          )}

          {/* Additional Actions Menu */}
          {hasApps && (
            <>
              <div className="h-6 w-px bg-gray-300 dark:bg-gray-600" />
              
              <div className="flex items-center space-x-1">
                {/* Share */}
                <button
                  className="inline-flex items-center px-2 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors duration-200"
                  title="Share plan"
                >
                  <ShareIcon className="h-4 w-4" />
                </button>

                {/* Export */}
                <button
                  className="inline-flex items-center px-2 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors duration-200"
                  title="Export plan"
                >
                  <DownloadIcon className="h-4 w-4" />
                </button>

                {/* Deploy/Test */}
                {!readonly && (
                  <button
                    className="inline-flex items-center px-2 py-1.5 text-sm font-medium text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-md transition-colors duration-200"
                    title="Test connection"
                  >
                    <PlayIcon className="h-4 w-4" />
                  </button>
                )}

                {/* Settings */}
                <button
                  className="inline-flex items-center px-2 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors duration-200"
                  title="Plan settings"
                >
                  <CogIcon className="h-4 w-4" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Plan Status */}
      {hasApps && (
        <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
          <span>Status: {readonly ? 'Read-only' : 'Editing'}</span>
          {currentPlan.updatedAt && (
            <span>Last updated: {new Date(currentPlan.updatedAt).toLocaleDateString()}</span>
          )}
        </div>
      )}

      {/* Load Dialog */}
      {showLoadDialog && onLoad && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Load Connection Plan
              </h3>
              
              <div className="space-y-3 mb-6">
                {/* This would be populated with actual saved plans */}
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No saved plans found. Create and save a plan to load it later.
                </p>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowLoadDialog(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}